import { useEffect, useRef, useState } from 'react';
import type { DemoKey, DemoScript, Fx, Level, RoundProps, ViewProps } from '../kit.js';
import { BoardFrame, INK, OK, RADIUS, STROKE, TYPE, alpha, clamp, easier, mono, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import { ERA_SKIN } from '../../art.js';

/**
 * SIGNAL OFFICER · TUNE THE RADIO — turn the wheel until the static clears, and hold it there.
 *
 * An old receiver: a scope showing what comes in, a lit dial with its needle, a magic-eye lamp
 * that closes as the signal firms up, and a wide knurled wheel along the bottom. Dragging sideways
 * anywhere on the set turns the wheel (a thumb's sweep is a good part of the dial, so it tunes
 * finely one-handed). Far from the station there is only static, drawn as a slowly shifting jagged
 * line; closer, a clean wave grows out of it. On the station the wave is clear, and holding it
 * there for about a second locks it; the ring round the eye fills while it holds. There is nothing
 * to get wrong. Each round the station is narrower and fainter from afar. (A station that drifts,
 * the old round 3, is still in the rules; no round deals it since 6 Oct 2026.)
 *
 * A converted board (kit.tsx): the rules as plain functions, a small view state (`TuneView`), a
 * pure `View`, the `Board` that plays it, and a `demo` tuned by the rules themselves.
 */

export interface TuneSpec {
  /** Half the band round the station that counts as clear (fractions of the dial). */
  band: number;
  /** How far off the station can first be heard. */
  reach: number;
  /** How far the station wanders either way, and how long one swing takes (s); 0 = still. */
  drift: number;
  periodS: number;
  /** How long it must be held clear to lock. */
  holdMs: number;
}

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const TUNES: readonly TuneSpec[] = easier(
  [
    { band: 0.05, reach: 0.3, drift: 0, periodS: 0, holdMs: 900 },
    { band: 0.036, reach: 0.25, drift: 0, periodS: 0, holdMs: 1_000 },
  ],
);

/** Finger travel, in board units, that turns the dial from one end to the other. */
export const DIAL_UNITS = 420;

/** The needle's first place and the hidden station, always out of earshot of each other at the start. */
export function tuneDeal(level: Level, rand: () => number): { start: number; station: number; phase: number } {
  const spec = TUNES[level]!;
  const lo = 0.1 + spec.drift;
  const hi = 0.9 - spec.drift;
  const phase = rand() * Math.PI * 2;
  for (let i = 0; i < 60; i++) {
    const start = 0.1 + rand() * 0.8;
    const station = lo + rand() * (hi - lo);
    if (Math.abs(start - station) >= 0.34) return { start, station, phase };
  }
  const station = lo + rand() * (hi - lo);
  return { start: station < 0.5 ? station + 0.4 : station - 0.4, station, phase };
}

/** Where the station is `tSec` into the round: still, or on its slow swing. */
export function tuneStation(spec: TuneSpec, base: number, phase: number, tSec: number): number {
  if (spec.drift <= 0 || spec.periodS <= 0) return base;
  return base + spec.drift * Math.sin((tSec / spec.periodS) * Math.PI * 2 + phase);
}

/** How clear the signal is at `d` from the station: 1 on it, fading to 0 at `reach`, and a clear step down just outside the band. */
export function tuneClarity(d: number, spec: TuneSpec): number {
  const off = Math.abs(d);
  if (off <= spec.band) return 1;
  const x = (off - spec.band) / (spec.reach - spec.band);
  if (x >= 1) return 0;
  return 0.8 * Math.pow(1 - x, 1.7);
}

/** Off the station the hold drains this much quicker than it builds. */
const DRAIN = 1.5;

/** One frame of the hold: it builds on the station and drains off it. */
export function stepHold(hold: number, dtMs: number, on: boolean): number {
  return on ? hold + dtMs : Math.max(0, hold - dtMs * DRAIN);
}

/** Everything the set shows. */
export interface TuneView {
  /** The game clock, ms: the static and the wave move with it. */
  t: number;
  /** The dial, 0–1 across (88–108). */
  f: number;
  /** How clear the signal is, 0–1 (1: on the station). */
  c: number;
  /** How long it has been held clear, 0–1 of what locks it. */
  h: number;
  /** 0 not yet turned, 1 turning, 2 locked. */
  s: 0 | 1 | 2;
  fx?: Fx | undefined;
}

// The set, in board units.
const SCOPE = { x: 18, y: 16, w: 284, h: 92 } as const;
const TRACE_N = 60;
const EYE = { x: 46, y: 172 } as const;
const DIAL = { x: 90, y: 142, w: 212, h: 60 } as const;
const WHEEL = { x: 28, y: 222, w: 264, h: 50 } as const;
/** How many sets of static the line eases between, and how often it moves on (per second). */
const NOISE_SETS = 12;
const NOISE_RATE = 1.3;
/** The static, fixed once for every board, so the View draws it from the clock alone. */
const NOISE: readonly (readonly number[])[] = (() => {
  const rand = seeded(4_513);
  return Array.from({ length: NOISE_SETS }, () => Array.from({ length: TRACE_N }, () => rand() * 2 - 1));
})();

/** The set, from its view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<TuneView>) {
  const skin = ERA_SKIN[era];
  const id = useSvgId();
  const state = Math.round(view.s);
  const locked = state === 2;
  const t = view.t / 1000;
  const f = clamp(view.f, 0, 1);
  const clear = locked ? 1 : clamp(view.c, 0, 1);
  const on = clear >= 0.999;
  const held = locked ? 1 : clamp(view.h, 0, 1);

  // The trace: a clean wave that grows with the signal, and static that shrinks with it. The static
  // eases from one fixed set to the next, so it drifts like a slow jagged line and never flickers.
  const k = Math.floor(t * NOISE_RATE);
  const blend = t * NOISE_RATE - k;
  const ease = blend * blend * (3 - 2 * blend);
  const a = NOISE[((k % NOISE_SETS) + NOISE_SETS) % NOISE_SETS]!;
  const b = NOISE[(((k + 1) % NOISE_SETS) + NOISE_SETS) % NOISE_SETS]!;
  const mid = SCOPE.y + SCOPE.h / 2;
  // The wave comes up out of the static quickly once there is any signal, so "closer" is plain to see.
  const waveGain = Math.sqrt(clear);
  const hissGain = Math.pow(1 - clear, 1.5);
  const trace = Array.from({ length: TRACE_N }, (_, i) => {
    const u = i / (TRACE_N - 1);
    const x = SCOPE.x + 8 + u * (SCOPE.w - 16);
    const hiss = a[i]! + (b[i]! - a[i]!) * ease + 0.3 * Math.sin(u * 11 + t * 0.8);
    const wave = Math.sin(u * Math.PI * 2 * 3 + t * 2.4);
    const y = mid - (34 * waveGain * wave + 28 * hissGain * hiss) * (i === 0 || i === TRACE_N - 1 ? 0.3 : 1);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');

  const needleX = DIAL.x + 12 + f * (DIAL.w - 24);
  const freq = (88 + f * 20).toFixed(1);

  // The wheel's ribs turn with the dial: the surface under the thumb moves as far as the thumb does.
  const radius = WHEEL.w / 2;
  const cx = WHEEL.x + radius;
  const spacing = 0.13;
  const turn = ((f * DIAL_UNITS) / radius) % spacing;
  const ribs: { x: number; depth: number }[] = [];
  for (let th = -Math.PI / 2 + turn; th < Math.PI / 2; th += spacing) {
    const depth = Math.cos(th);
    if (depth > 0.06) ribs.push({ x: cx + radius * Math.sin(th), depth });
  }

  // The magic eye: its dark wedge closes as the signal firms up.
  const wedge = ((1 - clear) * 64 + 3) * (Math.PI / 180);
  const eyeR = 17;
  const wedgePath = `M${EYE.x} ${EYE.y} L${EYE.x - eyeR * Math.sin(wedge)} ${EYE.y - eyeR * Math.cos(wedge)} A ${eyeR} ${eyeR} 0 0 1 ${EYE.x + eyeR * Math.sin(wedge)} ${EYE.y - eyeR * Math.cos(wedge)} Z`;
  const ringR = 25;
  const ringLen = 2 * Math.PI * ringR;

  const status = locked
    ? { text: say(lang, '✓ STATION FOUND', '✓ KANALEN FUNNET'), tone: 'ok' as const }
    : on
      ? { text: say(lang, 'CLEAR — HOLD STILL', 'KLART – HOLD STILLE'), tone: 'ok' as const }
      : clear > 0.02
        ? { text: say(lang, 'A SIGNAL', 'ET SIGNAL'), tone: 'warn' as const, hint: say(lang, 'TURN SLOWLY', 'DREI SAKTE') }
        : { text: say(lang, 'ONLY STATIC', 'BARE SUS'), hint: state === 1 ? say(lang, 'KEEP TURNING', 'DREI VIDERE') : say(lang, 'DRAG SIDEWAYS', 'DRA SIDELENGS') };

  const line = locked || on ? INK.hi : skin.haze;
  const panel = { fill: alpha(INK.onFill, 0.85), stroke: alpha(skin.ink, 0.35), strokeWidth: STROKE.hair };

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <radialGradient id={`${id}s`} cx=".5" cy=".5" r=".7">
          <stop offset="0" stopColor={skin.ink} stopOpacity=".16" />
          <stop offset="1" stopColor={INK.onFill} stopOpacity=".95" />
        </radialGradient>
        <linearGradient id={`${id}g`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={skin.haze} stopOpacity=".16" />
          <stop offset=".6" stopColor={skin.haze} stopOpacity=".06" />
          <stop offset="1" stopColor={INK.onFill} stopOpacity=".3" />
        </linearGradient>
        <linearGradient id={`${id}w`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={INK.onFill} />
          <stop offset=".5" stopColor={skin.ink} stopOpacity=".42" />
          <stop offset="1" stopColor={INK.onFill} />
        </linearGradient>
        <linearGradient id={`${id}sh`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={INK.hi} stopOpacity=".16" />
          <stop offset=".35" stopColor={INK.hi} stopOpacity="0" />
          <stop offset="1" stopColor={INK.onFill} stopOpacity=".35" />
        </linearGradient>
        <radialGradient id={`${id}e`} cx=".5" cy=".5" r=".5">
          <stop offset=".2" stopColor={INK.hi} stopOpacity=".75" />
          <stop offset=".55" stopColor={OK} />
          <stop offset="1" stopColor={OK} stopOpacity=".7" />
        </radialGradient>
        <clipPath id={`${id}c`}>
          <rect x={SCOPE.x} y={SCOPE.y} width={SCOPE.w} height={SCOPE.h} rx="8" />
        </clipPath>
      </defs>

      {/* the scope */}
      <rect x={SCOPE.x - 8} y={SCOPE.y - 8} width={SCOPE.w + 16} height={SCOPE.h + 16} rx={RADIUS.button + 3} {...panel} />
      <rect x={SCOPE.x} y={SCOPE.y} width={SCOPE.w} height={SCOPE.h} rx="8" fill={`url(#${id}s)`} />
      <g clipPath={`url(#${id}c)`}>
        {Array.from({ length: 11 }, (_, i) => (
          <line key={`v${i}`} x1={SCOPE.x + (i + 1) * (SCOPE.w / 12)} y1={SCOPE.y} x2={SCOPE.x + (i + 1) * (SCOPE.w / 12)} y2={SCOPE.y + SCOPE.h} stroke={skin.haze} strokeOpacity=".07" />
        ))}
        {[1, 2, 3].map((i) => (
          <line key={`h${i}`} x1={SCOPE.x} y1={SCOPE.y + (i * SCOPE.h) / 4} x2={SCOPE.x + SCOPE.w} y2={SCOPE.y + (i * SCOPE.h) / 4} stroke={skin.haze} strokeOpacity={i === 2 ? 0.14 : 0.07} />
        ))}
        <polyline points={trace} fill="none" stroke={line} strokeOpacity=".22" strokeWidth="6" strokeLinejoin="round" strokeLinecap="round" />
        <polyline points={trace} fill="none" stroke={line} strokeWidth={STROKE.line} strokeLinejoin="round" strokeLinecap="round" />
      </g>

      {/* the magic eye, and the ring that fills while it is held */}
      <circle cx={EYE.x} cy={EYE.y} r="30" {...panel} />
      <circle cx={EYE.x} cy={EYE.y} r={ringR} fill="none" stroke={INK.hi} strokeOpacity=".1" strokeWidth={STROKE.bold} />
      <circle
        cx={EYE.x}
        cy={EYE.y}
        r={ringR}
        fill="none"
        stroke={locked ? OK : accent}
        strokeWidth={STROKE.bold}
        strokeLinecap="round"
        strokeDasharray={`${(held * ringLen).toFixed(1)} ${ringLen.toFixed(1)}`}
        transform={`rotate(-90 ${EYE.x} ${EYE.y})`}
      />
      <circle cx={EYE.x} cy={EYE.y} r={eyeR} fill={`url(#${id}e)`} opacity={0.35 + 0.55 * clear} />
      <path d={wedgePath} fill={INK.onFill} />
      <circle cx={EYE.x} cy={EYE.y} r="6" fill={INK.onFill} stroke={INK.hi} strokeOpacity=".15" />
      <circle cx={EYE.x - 2} cy={EYE.y - 2} r="1.6" fill={INK.hi} opacity=".35" />

      {/* the dial glass, its scale and the needle, and where it stands above it */}
      <text x={DIAL.x + DIAL.w - 4} y={DIAL.y - 7} textAnchor="end" fontSize={TYPE.hint} fontFamily={mono} letterSpacing="1" fill={skin.haze} opacity=".8">
        {freq} MHz
      </text>
      <rect x={DIAL.x} y={DIAL.y} width={DIAL.w} height={DIAL.h} rx={RADIUS.button} {...panel} />
      <rect x={DIAL.x + 3} y={DIAL.y + 3} width={DIAL.w - 6} height={DIAL.h - 6} rx={RADIUS.button - 2} fill={`url(#${id}g)`} />
      {Array.from({ length: 41 }, (_, i) => {
        const x = DIAL.x + 12 + (i / 40) * (DIAL.w - 24);
        const major = i % 8 === 0;
        return (
          <line key={i} x1={x} y1={DIAL.y + 9} x2={x} y2={DIAL.y + (major ? 24 : i % 2 === 0 ? 18 : 14)} stroke={skin.haze} strokeOpacity={major ? 0.7 : 0.35} strokeWidth={major ? 1.3 : 1} />
        );
      })}
      {Array.from({ length: 6 }, (_, i) => (
        <text key={i} x={DIAL.x + 12 + (i / 5) * (DIAL.w - 24)} y={DIAL.y + 40} textAnchor="middle" fontSize={TYPE.hint} fontFamily={mono} fill={skin.haze} opacity=".7">
          {88 + i * 4}
        </text>
      ))}
      <line x1={DIAL.x + 12} y1={DIAL.y + 50} x2={DIAL.x + DIAL.w - 12} y2={DIAL.y + 50} stroke={skin.haze} strokeOpacity=".2" />
      <rect x={needleX - 3} y={DIAL.y + 5} width="6" height={DIAL.h - 10} rx="3" fill={accent} opacity=".25" />
      <line x1={needleX} y1={DIAL.y + 6} x2={needleX} y2={DIAL.y + DIAL.h - 6} stroke={accent} strokeWidth="2.4" strokeLinecap="round" />

      {/* the wheel: a knurled drum in its housing */}
      <rect x={WHEEL.x - 14} y={WHEEL.y - 10} width={WHEEL.w + 28} height={WHEEL.h + 40} rx={RADIUS.board - 2} {...panel} />
      <rect x={WHEEL.x} y={WHEEL.y} width={WHEEL.w} height={WHEEL.h} rx="6" fill={`url(#${id}w)`} />
      {ribs.map((rib, i) => (
        <g key={i}>
          <line x1={rib.x} y1={WHEEL.y + 3} x2={rib.x} y2={WHEEL.y + WHEEL.h - 3} stroke={INK.onFill} strokeOpacity={0.35 + 0.5 * rib.depth} strokeWidth={0.8 + 2.4 * rib.depth} />
          <line
            x1={rib.x + 1.6 * rib.depth}
            y1={WHEEL.y + 3}
            x2={rib.x + 1.6 * rib.depth}
            y2={WHEEL.y + WHEEL.h - 3}
            stroke={skin.haze}
            strokeOpacity={0.08 + 0.3 * rib.depth}
            strokeWidth={0.6 + 0.8 * rib.depth}
          />
        </g>
      ))}
      <rect x={WHEEL.x} y={WHEEL.y} width={WHEEL.w} height={WHEEL.h} rx="6" fill={`url(#${id}sh)`} />
      <text x={cx} y={WHEEL.y + WHEEL.h + 21} textAnchor="middle" fontSize={TYPE.hint} letterSpacing="2" fontFamily={mono} fill={skin.haze} opacity=".65">
        {say(lang, '◀  DRAG TO TURN  ▶', '◀  DRA FOR Å DREIE  ▶')}
      </text>
    </BoardFrame>
  );
}

function Board(props: RoundProps) {
  const { level, lang } = props;
  const spec = TUNES[level]!;
  const [deal] = useState(() => tuneDeal(level, seeded(Date.now() + level * 104_729)));
  const dial = useRef(deal.start);
  const moved = useRef(false);
  const station = useRef(deal.station);
  const hold = useRef(0);
  const best = useRef(0);
  /** Seconds into the round, on the game clock: the station's drift. */
  const clock = useRef(0);
  /** Where the finger last was across the board while it turns the wheel, or null. */
  const drag = useRef<number | null>(null);

  const round = useRound(props, (dt, _now, won) => {
    clock.current += dt;
    station.current = tuneStation(spec, deal.station, deal.phase, clock.current);
    if (won) return;
    const d = dial.current - station.current;
    // On the station the hold builds; off it, it drains quicker than it came.
    hold.current = stepHold(hold.current, dt * 1000, Math.abs(d) <= spec.band);
    const progress = Math.min(0.99, tuneClarity(d, spec) * 0.75 + (hold.current / spec.holdMs) * 0.25);
    if (progress > best.current + 0.02) {
      best.current = progress;
      round.progress(progress);
    }
    if (hold.current >= spec.holdMs) round.win();
  });

  useEffect(() => {
    if (props.paused) drag.current = null;
  }, [props.paused]);

  const d = dial.current - station.current;
  const view: TuneView = {
    t: q(round.now),
    f: q(dial.current, 3),
    c: round.won ? 1 : q(tuneClarity(d, spec), 2),
    h: round.won ? 1 : q(Math.min(1, hold.current / spec.holdMs), 2),
    s: round.won ? 2 : moved.current ? 1 : 0,
    fx: round.fx,
  };
  return (
    <View
      view={round.show(view)}
      era={props.era}
      accent={props.accent}
      lang={lang}
      input={{
        label: say(lang, 'Drag sideways to tune', 'Dra sidelengs for å stille inn'),
        down: (p) => {
          if (round.live) drag.current = p.x;
        },
        move: (p) => {
          if (drag.current === null || !round.live) return;
          dial.current = clamp(dial.current + (p.x - drag.current) / DIAL_UNITS, 0, 1);
          drag.current = p.x;
          moved.current = true;
        },
        up: () => {
          drag.current = null;
        },
      }}
    />
  );
}

/** The how-to's finger along the wheel: [ms, across]. It presses at the first and lets go after the last. */
const DEMO_PATH = [
  [700, 70],
  [2_700, 249],
  [3_400, 220],
] as const;
const DEMO_Y = WHEEL.y + WHEEL.h / 2;
const DEMO_START = 0.2;
const DEMO_STATION = 0.56;

/** Where the demo's finger is across, as the demo player moves it (eased between its keys). */
function demoFingerX(t: number): number {
  if (t <= DEMO_PATH[0][0]) return DEMO_PATH[0][1];
  for (let i = 1; i < DEMO_PATH.length; i++) {
    const [t0, x0] = DEMO_PATH[i - 1]!;
    const [t1, x1] = DEMO_PATH[i]!;
    if (t < t1) {
      const u = (t - t0) / (t1 - t0);
      return x0 + (x1 - x0) * u * u * (3 - 2 * u);
    }
  }
  return DEMO_PATH[DEMO_PATH.length - 1]![1];
}

/**
 * The how-to, tuned by the rules themselves: round 1, the finger drags along the wheel, the static
 * gives way to a wave, it goes a little past and comes back onto the station, and holds still
 * until the ring fills and the station locks. Worked out once and keyed every tenth of a second.
 */
function demoTuning(): DemoScript<TuneView> {
  const spec = TUNES[0]!;
  const ms = 7_000;
  const keys: DemoKey<TuneView>[] = [];
  let hold = 0;
  let lockedAt: number | null = null;
  let heard: number | null = null;
  const dt = 1000 / 60;
  for (let frame = 0; frame * dt <= ms; frame++) {
    const t = frame * dt;
    const f = clamp(DEMO_START + (demoFingerX(t) - DEMO_PATH[0][1]) / DIAL_UNITS, 0, 1);
    const d = f - DEMO_STATION;
    const clear = tuneClarity(d, spec);
    if (heard === null && clear > 0.02) heard = t;
    if (lockedAt === null) {
      hold = stepHold(hold, dt, Math.abs(d) <= spec.band);
      if (hold >= spec.holdMs) lockedAt = t;
    }
    if (frame % 6 === 0) {
      const locked = lockedAt !== null;
      keys.push({
        at: Math.round(t),
        view: {
          t: 0,
          f: q(f, 3),
          c: locked ? 1 : q(clear, 2),
          h: locked ? 1 : q(Math.min(1, hold / spec.holdMs), 2),
          s: locked ? 2 : t >= DEMO_PATH[0][0] ? 1 : 0,
          fx: locked && t - lockedAt! < 450 ? [q(1 - (t - lockedAt!) / 450, 2), 0] : [0, 0],
        },
      });
    }
  }
  const [, last] = DEMO_PATH[DEMO_PATH.length - 1]!;
  const lift = Math.round((lockedAt ?? 4_400) + 300);
  return {
    ms,
    keys,
    finger: [
      { at: 0, x: DEMO_PATH[0][1], y: DEMO_Y, hide: true },
      { at: 300, x: DEMO_PATH[0][1], y: DEMO_Y, hide: false },
      { at: DEMO_PATH[0][0], x: DEMO_PATH[0][1], y: DEMO_Y, down: true },
      ...DEMO_PATH.slice(1).map(([at, x]) => ({ at, x, y: DEMO_Y })),
      { at: lift, x: last, y: DEMO_Y, down: false },
      { at: lift + 600, x: last + 20, y: DEMO_Y + 30, hide: true },
    ],
    steps: [
      { at: 0, en: 'DRAG SIDEWAYS TO TURN', no: 'DRA SIDELENGS FOR Å DREIE' },
      { at: Math.round(Math.max(1_200, heard ?? 1_200)), en: 'TURN UNTIL THE WAVE IS CLEAR', no: 'DREI TIL BØLGEN ER KLAR' },
      { at: DEMO_PATH[2][0] - 300, en: 'HOLD STILL TO LOCK IT', no: 'HOLD STILLE FOR Å LÅSE' },
    ],
  };
}

export const game = thirtyGame<TuneView>({
  Board,
  View,
  demo: demoTuning(),
  // Nothing to get wrong here: no mistake count under the board.
  mistakes: false,
  words: {
    en: {
      instruction: 'Drag sideways to turn the dial. When the static clears, hold still to lock on.',
      howMove: 'Drag your finger left or right to tune. Hold still when the wave is clear.',
    },
    no: {
      instruction: 'Dra sidelengs for å dreie. Når suset forsvinner, hold stille for å låse kanalen.',
      howMove: 'Dra fingeren til venstre eller høyre. Hold stille når bølgen er klar.',
    },
  },
});
